import React from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import LockIcon from '@mui/icons-material/Lock';
import DialogContent from '@mui/material/DialogContent';
import range from 'lodash/range';
import PropTypes from 'prop-types';
import {
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
} from '@/components/common/CustomDialog';
import ControlledPrefixInput from '@/components/common/form/ControlledPrefixInput';
import ControlledTextField from '@/components/common/form/ControlledTextField';
import LabelSelect from '@/components/common/form/LabelSelect';
import IntegrationID from '@/constants/integrations/IntegrationID';
import { useCurrentUser } from '@/lib/hooks/userData';
import translate from '@/lib/intl/translate';
import { ConstituentType } from '@/lib/types/ConstituentPropType';
import FileUploader from '@/modules/FileUploader';
import ConstituentField from '../constants/ConstituentField';
import LockedFormHelperTextProps from '../constants/LockedFormHelperTextProps';
import TemporaryField from '../constants/TemporaryField';
import useConstituentEditForm from '../useConstituentEditForm';
import AdditionalFields from './AdditionalFields';
import AddressFields from './AddressFields';
import AvatarField from './AvatarField';
import CategorySelect from './CategorySelect';
import CustomFields from './CustomFields';
import EmailFields from './EmailFields';
import LockedSalesforceHelperText from './LockedSalesforceHelperText';
import PhoneFields from './PhoneFields';

function ConstituentEditForm({ constituentId, constituentType, onCancel }) {
  const { isOrgAdmin, isOrgEditor } = useCurrentUser();
  const isOrgType = constituentType === ConstituentType.ORG;

  const { extSource, formMethods, lockedAttributes, onSubmit } =
    useConstituentEditForm({
      constituentId,
      constituentType,
    });

  const {
    control,
    getValues,
    formState: { errors, isSubmitting },
    setValue,
  } = formMethods;

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={onSubmit} noValidate data-testid="edit-constituent-form">
        <DialogContent
          dividers
          className="grid w-full grid-cols-1 items-start gap-x-4 gap-y-6 p-4 lg:grid-cols-4 lg:py-6 lg:pl-12 lg:pr-24"
        >
          {extSource === IntegrationID.SALESFORCE && (
            <div className="absolute top-4 right-4 flex items-center gap-1 text-grey-900 type-body1 lg:right-8">
              <span>{translate('INTEGRATIONS_SALESFORCE')}</span>

              <LockIcon className="h-[18px] w-[18px]" />
            </div>
          )}

          <div className="col-span-full">
            {translate(
              isOrgType
                ? 'DONOR_COMPANY_INFORMATION'
                : 'DONOR_PERSONAL_INFORMATION',
            )}
          </div>
          <AvatarField />
          {isOrgType ? (
            <>
              <ControlledTextField
                className="col-span-full"
                control={control}
                disabled={lockedAttributes.has(ConstituentField.ORG_NAME)}
                error={!!errors[ConstituentField.ORG_NAME]}
                helperText={
                  errors[ConstituentField.ORG_NAME]?.message || (
                    <LockedSalesforceHelperText
                      locked={lockedAttributes.has(ConstituentField.ORG_NAME)}
                    />
                  )
                }
                label={translate('COMPANY_NAME_LABEL')}
                name={ConstituentField.ORG_NAME}
                rules={{ required: translate('FORMS_VALIDATION_REQUIRED') }}
                required
                FormHelperTextProps={LockedFormHelperTextProps}
              />
              <ControlledTextField
                className="col-span-full"
                control={control}
                label={translate('DONOR_INDUSTRY_LABEL')}
                name={ConstituentField.INDUSTRY}
              />
            </>
          ) : (
            <>
              <ControlledTextField
                className="lg:col-span-2"
                control={control}
                disabled={lockedAttributes.has(ConstituentField.FIRST_NAME)}
                label={translate('DONOR_FIRST_NAME')}
                error={!!errors[ConstituentField.FIRST_NAME]}
                helperText={
                  errors[ConstituentField.FIRST_NAME]?.message || (
                    <LockedSalesforceHelperText
                      locked={lockedAttributes.has(ConstituentField.FIRST_NAME)}
                    />
                  )
                }
                name={ConstituentField.FIRST_NAME}
                rules={{ required: translate('FORMS_VALIDATION_REQUIRED') }}
                required
                {...(lockedAttributes.has(ConstituentField.FIRST_NAME) && {
                  FormHelperTextProps: LockedFormHelperTextProps,
                })}
              />
              <ControlledTextField
                className="lg:col-span-2"
                control={control}
                disabled={lockedAttributes.has(ConstituentField.LAST_NAME)}
                helperText={
                  <LockedSalesforceHelperText
                    locked={lockedAttributes.has(ConstituentField.LAST_NAME)}
                  />
                }
                label={translate('DONOR_LAST_NAME')}
                name={ConstituentField.LAST_NAME}
                FormHelperTextProps={LockedFormHelperTextProps}
              />
              <ControlledTextField
                className="lg:col-span-2"
                control={control}
                label={translate('DONOR_PREFERRED_FIRST_NAME')}
                name={ConstituentField.PREFERRED_FIRST_NAME}
              />
              <ControlledPrefixInput
                className="lg:col-span-1"
                control={control}
                disabled={lockedAttributes.has(ConstituentField.PREFIX)}
                helperText={
                  <LockedSalesforceHelperText
                    locked={lockedAttributes.has(ConstituentField.PREFIX)}
                  />
                }
                label={translate('DONOR_PREFIX')}
                name={ConstituentField.PREFIX}
                FormHelperTextProps={LockedFormHelperTextProps}
              />
              <ControlledTextField
                className="lg:col-span-1"
                control={control}
                disabled={lockedAttributes.has(ConstituentField.PRONOUNS)}
                helperText={
                  <LockedSalesforceHelperText
                    locked={lockedAttributes.has(ConstituentField.PRONOUNS)}
                  />
                }
                label={translate('PRONOUNS_LABEL')}
                name={ConstituentField.PRONOUNS}
                FormHelperTextProps={LockedFormHelperTextProps}
              />
            </>
          )}
          <div className="col-span-full mt-4">
            {translate('DONOR_CONTACTS_LABEL')}
          </div>

          <EmailFields locked={lockedAttributes.has(ConstituentField.EMAILS)} />
          <PhoneFields
            isOrgType={isOrgType}
            locked={lockedAttributes.has(ConstituentField.PHONES)}
          />
          <AddressFields
            locked={lockedAttributes.has(ConstituentField.ADDRESSES)}
          />

          <div className="col-span-full mt-4">
            {translate('ADDITIONAL_INFORMATION')}
          </div>
          <ControlledTextField
            className="col-span-full"
            control={control}
            disabled={lockedAttributes.has(ConstituentField.BIOGRAPHY)}
            helperText={
              <LockedSalesforceHelperText
                locked={lockedAttributes.has(ConstituentField.BIOGRAPHY)}
              />
            }
            label={translate('BIOGRAPHY_LABEL')}
            inputProps={{ maxLength: 5000 }}
            minRows={3}
            multiline
            name={ConstituentField.BIOGRAPHY}
            FormHelperTextProps={LockedFormHelperTextProps}
          />
          <CategorySelect className="col-span-full" />
          <Controller
            control={control}
            name={TemporaryField.LABELS}
            render={({ field: { onChange, value } }) => (
              <LabelSelect
                className="col-span-full"
                onChange={onChange}
                freeSolo={isOrgAdmin || isOrgEditor}
                value={value}
              />
            )}
          />
          <AdditionalFields lockedAttributes={lockedAttributes} />
          <CustomFields />

          <div className="col-span-full mt-4">
            {translate('THREE_KEY_NOTES_LABEL')}
          </div>
          {range(1, 4).map((num) => (
            <ControlledTextField
              className="col-span-full"
              control={control}
              key={num}
              label={translate(`CONSTITUENTS_NOTE_PLACEHOLDER_${num}`)}
              name={ConstituentField[`NOTE_${num}`]}
            />
          ))}

          <div className="col-span-full mt-4">{translate('FILES')}</div>
          <FileUploader
            className="col-span-full"
            multiple
            onUploadFinished={(e) =>
              setValue(
                TemporaryField.UPLOAD_IDS,
                e.map(({ uploadId }) => uploadId),
              )
            }
            value={getValues(ConstituentField.ATTACHMENTS)}
          />
        </DialogContent>
        <DialogActions>
          <DialogActionButton
            data-testid="constituent-modal-save-button"
            showLoader={isSubmitting}
            disabled={isSubmitting}
            type="submit"
          >
            {translate('SAVE')}
          </DialogActionButton>
          <DialogCancelButton
            data-testid="constituent-modal-cancel-button"
            onClick={onCancel}
          >
            {translate('CANCEL')}
          </DialogCancelButton>
          {errors?.root?.serverError?.message && (
            <div className="px-2 text-error-main">
              {errors.root.serverError.message}
            </div>
          )}
        </DialogActions>
      </form>
    </FormProvider>
  );
}

ConstituentEditForm.propTypes = {
  constituentId: PropTypes.string,
  constituentType: PropTypes.oneOf(Object.values(ConstituentType)).isRequired,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
};

ConstituentEditForm.defaultProps = {
  constituentId: null,
  onCancel: () => {},
  onSave: () => {},
};

export default ConstituentEditForm;
